import { useState } from 'react'
import { speak } from '../lib/speech'
import { languageOf } from '../lib/languages'
import { useSettings } from '../state/store'

/**
 * Plays a line aloud, optionally with a second button that plays it slowly.
 *
 * Both read the same everywhere in the app — “Play” and “Slower” — so the pair
 * is recognised by shape rather than re-read on each screen. A control that
 * renames itself per context is a control you have to think about.
 *
 * The slow button exists because the moment you need a slower reading is the
 * moment you are listening — not a trip to Settings, a change, a walk back, and
 * a replay by which time you have forgotten what you were struggling with. It
 * uses its own stored rate so a comfortable listening pace and a
 * pull-it-apart pace can both exist at once.
 *
 * If no voice for the language is installed the button says so rather than
 * doing nothing: a silent no-op reads as a broken app, and the fix — install a
 * language pack — is not something the page can do for you. It says so only
 * once that is actually known: browsers load their voice list asynchronously,
 * and an empty list on a first click means "not yet", not "not installed".
 */
export function SpeakButton({
  text,
  language = 'zh',
  label = 'Play',
  rate,
  withSlow = false,
  className = 'btn is-small',
}: {
  text: string
  /** Which language this text is in, so the right voice is chosen. */
  language?: string
  label?: string
  rate?: number
  /** Show a companion button that plays the same line slowly. */
  withSlow?: boolean
  className?: string
}) {
  const settings = useSettings()
  const lang = languageOf(language)
  const [failed, setFailed] = useState(false)

  // The slow button is a peer of the one it sits beside, so it wears the same
  // button — same size, same weight — rather than a quiet variant that reads as
  // a link. Emphasis is the one thing it does not inherit: two primaries side by
  // side would each be asking to be pressed first.
  const slowClass = className
    .split(/\s+/)
    .filter((c) => c && c !== 'is-primary' && c !== 'is-live')
    .join(' ')

  const play = (chosenRate: number) => {
    const ok = speak(text, {
      speech: lang.speech,
      voiceURI: settings.voiceByLanguage?.[lang.code] ?? null,
      rate: chosenRate,
      // Reported late, when the browser's voice list arrives and this language
      // is not in it. On a cold page the synchronous return cannot know yet.
      onUnavailable: () => setFailed(true),
    })
    setFailed(!ok)
  }

  return (
    <span className="speak-group">
      <button
        type="button"
        className={className}
        onClick={() => play(rate ?? settings.speechRate)}
        title={failed ? `No ${lang.name} voice installed — see Settings` : 'Play this line'}
      >
        {failed ? `No ${lang.code} voice` : label}
      </button>

      {withSlow && !failed && (
        <button
          type="button"
          className={slowClass}
          onClick={() => play(settings.slowRate)}
          title={`Slower (${settings.slowRate.toFixed(2)}×)`}
          aria-label="Slower"
        >
          Slower
        </button>
      )}
    </span>
  )
}
